import { useState, useEffect } from 'react';
import { admin as adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Hotel, Users, CalendarDays, DollarSign, ShieldCheck, AlertTriangle,
  CheckCircle, XCircle, BarChart3, Clock, ArrowLeft, Ban, Calendar, Send, Mail, Trash2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AdminVerificationQueue } from '@/components/verification/AdminVerificationQueue';
import { BACKEND_URL } from '@/lib/backendUrl';

function GlassCard({ children, className = '' }) {
  return (
    <div className={`glass rounded-2xl p-5 shadow-2xl ${className}`}>{children}</div>
  );
}

function AdminAvailabilityTab() {
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHotels, setLoadingHotels] = useState(true);

  useEffect(() => {
    loadHotels();
  }, []);

  useEffect(() => {
    if (selectedHotel) {
      loadBlockedDates();
    }
  }, [selectedHotel]);

  const loadHotels = async () => {
    try {
      setLoadingHotels(true);
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/hotels`);
      const data = await response.json();
      // API returns array directly, not wrapped in .data
      setHotels(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load hotels:', error);
      toast.error('Failed to load hotels');
    } finally {
      setLoadingHotels(false);
    }
  };

  const loadBlockedDates = async () => {
    if (!selectedHotel) return;
    
    try {
      setLoading(true);
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/availability/blocked/${selectedHotel}`);
      const data = await response.json();
      setBlockedDates(data.blocked_dates || []);
    } catch (error) {
      console.error('Failed to load blocked dates:', error);
      toast.error('Failed to load blocked dates');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockDates = async () => {
    if (!selectedHotel || !startDate || !endDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/availability/block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: selectedHotel,
          start_date: startDate,
          end_date: endDate,
          reason: reason || 'Manual block by admin',
          blocked_by: 'platform_admin',
          source: 'admin_dashboard'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Successfully blocked ${data.nights_blocked} night(s)`);
        setStartDate('');
        setEndDate('');
        setReason('');
        loadBlockedDates();
      } else {
        throw new Error(data.detail || 'Failed to block dates');
      }
    } catch (error) {
      console.error('Error blocking dates:', error);
      toast.error(error.message || 'Failed to block dates');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (blockId) => {
    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/availability/unblock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ block_id: blockId })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Date block removed');
        loadBlockedDates();
      } else {
        throw new Error(data.detail || 'Failed to unblock');
      }
    } catch (error) {
      console.error('Error unblocking:', error);
      toast.error('Failed to unblock dates');
    }
  };

  if (loadingHotels) {
    return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-availability-tab">
      {/* Block Dates Form */}
      <GlassCard>
        <h3 className="font-semibold heading-font mb-6 flex items-center gap-2">
          <Ban className="w-5 h-5 text-primary" /> Block Hotel Dates
        </h3>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="hotel-select">Select Hotel</Label>
            <Select value={selectedHotel} onValueChange={setSelectedHotel}>
              <SelectTrigger id="hotel-select" data-testid="admin-availability-hotel-select">
                <SelectValue placeholder="Choose a hotel..." />
              </SelectTrigger>
              <SelectContent>
                {hotels.map(hotel => (
                  <SelectItem key={hotel.id} value={hotel.id}>
                    {hotel.name} ({hotel.city})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="admin-availability-start-date"
              />
            </div>
            <div>
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="admin-availability-end-date"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="block-reason">Reason (optional)</Label>
            <Input
              id="block-reason"
              placeholder="e.g., Maintenance, renovation, seasonal closure"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="admin-availability-reason-input"
            />
          </div>

          <Button 
            onClick={handleBlockDates}
            disabled={!selectedHotel || !startDate || !endDate || loading}
            className="w-full md:w-auto"
            data-testid="admin-availability-block-button"
          >
            <Ban className="w-4 h-4 mr-2" />
            {loading ? 'Blocking...' : 'Block Dates'}
          </Button>
        </div>
      </GlassCard>

      {/* Blocked Dates Table */}
      {selectedHotel && (
        <GlassCard>
          <h3 className="font-semibold heading-font mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" /> 
            Blocked Dates for {hotels.find(h => h.id === selectedHotel)?.name || 'Selected Hotel'}
          </h3>

          {loading && !blockedDates.length ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : blockedDates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No blocked dates for this hotel</p>
              <p className="text-xs mt-1">Use the form above to block dates</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Nights</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Blocked By</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blockedDates.map((block) => (
                  <TableRow key={block.id} data-testid={`blocked-date-${block.id}`}>
                    <TableCell className="font-mono text-xs">{block.start_date}</TableCell>
                    <TableCell className="font-mono text-xs">{block.end_date}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{block.nights_blocked} nights</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{block.reason}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{block.blocked_by}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {block.source === 'telegram' ? <Send className="w-3 h-3 mr-1" /> : 
                         block.source === 'email' ? <Mail className="w-3 h-3 mr-1" /> : 
                         <Calendar className="w-3 h-3 mr-1" />}
                        {block.source}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(block.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnblock(block.id)}
                        data-testid={`unblock-button-${block.id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Unblock
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </GlassCard>
      )}
    </div>
  );
}

function AdminBillingTab() {
  const [billingOverview, setBillingOverview] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const backendUrl = BACKEND_URL;
      const [overviewRes, invoicesRes] = await Promise.all([
        fetch(`${backendUrl}/api/billing/admin/overview`).then(r => r.json()),
        fetch(`${backendUrl}/api/billing/admin/invoices`).then(r => r.json())
      ]);
      setBillingOverview(overviewRes);
      setInvoices(invoicesRes.invoices || []);
    } catch (error) {
      console.error('Failed to load billing:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const markInvoicePaid = async (invoiceId) => {
    try {
      const backendUrl = BACKEND_URL;
      await fetch(`${backendUrl}/api/billing/admin/invoices/${invoiceId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'paid',
          payment_data: {
            payment_method: 'manual_verification',
            payment_notes: 'Marked paid by admin'
          }
        })
      });
      toast.success('Invoice marked as paid');
      loadBillingData();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const suspendHotel = async (hotelId, hotelName) => {
    if (!confirm(`Suspend ${hotelName} for non-payment?`)) return;

    try {
      const backendUrl = BACKEND_URL;
      await fetch(`${backendUrl}/api/billing/admin/hotels/${hotelId}/suspend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Overdue platform fees - Admin action' })
      });
      toast.success(`${hotelName} suspended`);
      loadBillingData();
    } catch (error) {
      toast.error('Failed to suspend hotel');
    }
  };

  const unsuspendHotel = async (hotelId, hotelName) => {
    try {
      const backendUrl = BACKEND_URL;
      await fetch(`${backendUrl}/api/billing/admin/hotels/${hotelId}/unsuspend`, {
        method: 'POST'
      });
      toast.success(`${hotelName} unsuspended`);
      loadBillingData();
    } catch (error) {
      toast.error('Failed to unsuspend hotel');
    }
  };

  if (loading) {
    return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Billing Overview KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <GlassCard>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Outstanding</p>
              <p className="text-2xl font-bold mono">${billingOverview?.total_outstanding?.toLocaleString() || '0'}</p>
            </div>
            <DollarSign className="w-8 h-8 text-orange-500 opacity-50" />
          </div>
        </GlassCard>

        <GlassCard>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Overdue</p>
              <p className="text-2xl font-bold mono">{billingOverview?.overdue_invoices || 0}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500 opacity-50" />
          </div>
        </GlassCard>

        <GlassCard>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Suspended</p>
              <p className="text-2xl font-bold mono">{billingOverview?.suspended_hotels || 0}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-500 opacity-50" />
          </div>
        </GlassCard>

        <GlassCard>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Paid This Month</p>
              <p className="text-2xl font-bold mono">{billingOverview?.paid_this_month || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
          </div>
        </GlassCard>
      </div>

      {/* Suspended Hotels Alert */}
      {billingOverview?.suspended_hotel_list?.length > 0 && (
        <GlassCard className="border-l-4 border-red-500">
          <h4 className="font-semibold text-red-500 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Suspended Hotels
          </h4>
          <div className="space-y-2">
            {billingOverview.suspended_hotel_list.map(hotel => (
              <div key={hotel.id} className="flex items-center justify-between text-sm">
                <span>{hotel.name}</span>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => unsuspendHotel(hotel.id, hotel.name)}
                >
                  Unsuspend
                </Button>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Invoices Table */}
      <GlassCard>
        <h3 className="font-semibold mb-4 heading-font">All Invoices</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead>Hotel</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Amount Due</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No invoices generated yet
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow key={invoice.id} data-testid={`admin-invoice-${invoice.id}`}>
                  <TableCell className="font-mono text-xs">{invoice.invoice_number}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm">{invoice.hotel_name}</p>
                      {invoice.hotel_suspended && (
                        <Badge variant="destructive" className="text-[10px] mt-1">Suspended</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{invoice.billing_period}</TableCell>
                  <TableCell className="font-mono font-semibold">${invoice.platform_fees_owed?.toLocaleString()}</TableCell>
                  <TableCell className="text-sm">{new Date(invoice.due_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Badge variant={
                      invoice.status === 'paid' ? 'default' :
                      invoice.status === 'overdue' ? 'destructive' :
                      invoice.status === 'pending_review' ? 'outline' : 'secondary'
                    }>
                      {invoice.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    {invoice.status === 'pending' || invoice.status === 'overdue' ? (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => markInvoicePaid(invoice.id)}
                          data-testid={`mark-paid-${invoice.id}`}
                        >
                          Mark Paid
                        </Button>
                        {!invoice.hotel_suspended && (
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => suspendHotel(invoice.hotel_id, invoice.hotel_name)}
                          >
                            Suspend
                          </Button>
                        )}
                      </>
                    ) : invoice.status === 'pending_review' ? (
                      <Button 
                        size="sm" 
                        variant="default"
                        onClick={() => markInvoicePaid(invoice.id)}
                      >
                        Verify & Approve
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </GlassCard>
    </div>
  );
}

export default function AdminDashboardPage({ embedded = false }) {
  const [stats, setStats] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, hotelsRes] = await Promise.all([
        adminApi.stats(),
        adminApi.hotels()
      ]);
      setStats(statsRes.data);
      setHotels(hotelsRes.data);
    } catch (e) { console.error(e); }
  };

  const verifyHotel = async (hotelId, status) => {
    try {
      await adminApi.verifyHotel(hotelId, { verification_status: status, trust_score: status === 'verified' ? 90 : 0 });
      toast.success(`Hotel ${status}`);
      loadData();
    } catch (e) { toast.error('Failed to update'); }
  };

  const filteredHotels = filter ? hotels.filter(h => h.verification_status === filter) : hotels;

  return (
    <div className={embedded ? '' : 'min-h-screen lobby-theme'} data-testid="admin-dashboard">
      {!embedded && (
      <nav className="border-b border-border bg-card/60 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></Link>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
                <Hotel className="w-3.5 h-3.5 text-primary-foreground" />
              </div>
              <span className="font-bold heading-font">Admin Dashboard</span>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">Platform Admin</Badge>
        </div>
      </nav>
      )}

      <main className={embedded ? 'px-4 lg:px-6 py-6' : 'max-w-[1400px] mx-auto px-6 py-8'}>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview"><BarChart3 className="w-4 h-4 mr-1" /> Overview</TabsTrigger>
            <TabsTrigger value="verification" data-testid="admin-verification-tab"><ShieldCheck className="w-4 h-4 mr-1" /> Verification</TabsTrigger>
            <TabsTrigger value="billing" data-testid="admin-billing-tab"><DollarSign className="w-4 h-4 mr-1" /> Billing</TabsTrigger>
            <TabsTrigger value="availability" data-testid="admin-availability-tab"><Ban className="w-4 h-4 mr-1" /> Availability</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            {stats && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {[
                    { title: 'Total Hotels', value: stats.total_hotels, icon: Hotel },
                    { title: 'Total Bookings', value: stats.total_bookings, icon: CalendarDays },
                    { title: 'Gross Revenue', value: `$${stats.total_revenue.toLocaleString()}`, icon: DollarSign },
                    { title: 'Platform Fees', value: `$${stats.platform_revenue.toLocaleString()}`, icon: BarChart3 },
                  ].map(({ title, value, icon: Icon }) => (
                    <GlassCard key={title}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground">{title}</p>
                          <p className="text-2xl font-bold heading-font mono mt-1">{value}</p>
                        </div>
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                    </GlassCard>
                  ))}
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <GlassCard>
                    <h3 className="font-semibold heading-font mb-4 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-primary" /> Revenue Breakdown
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Gross Revenue</span>
                        <span className="mono font-semibold">${stats.total_revenue.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Platform Fees</span>
                        <span className="mono font-semibold text-primary">${stats.platform_revenue.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Hotel Revenue</span>
                        <span className="mono font-semibold">${stats.hotel_revenue.toLocaleString()}</span>
                      </div>
                    </div>
                  </GlassCard>

                  <GlassCard>
                    <h3 className="font-semibold heading-font mb-4 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-primary" /> Verification Status
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Verified</span>
                        <Badge>{stats.verified_hotels}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Pending</span>
                        <Badge variant="secondary">{stats.pending_verification}</Badge>
                      </div>
                    </div>
                  </GlassCard>

                  <GlassCard>
                    <h3 className="font-semibold heading-font mb-4 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" /> Recent Hotels
                    </h3>
                    <div className="space-y-2">
                      {(stats.recent_hotels || []).slice(0, 5).map(h => (
                        <div key={h.id} className="flex justify-between items-center text-sm">
                          <span>{h.name}</span>
                          <Badge variant={h.verification_status === 'verified' ? 'default' : 'secondary'} className="text-[10px]">
                            {h.verification_status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </GlassCard>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="verification" className="mt-6">
            {/* Verification Queue for Guest IDs and Hotel Docs */}
            <div className="mb-8">
              <AdminVerificationQueue />
            </div>
            
            {/* Hotel Listing Verification (existing) */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold heading-font mb-2">Hotel Listing Verification</h3>
              <p className="text-sm text-muted-foreground mb-4">Approve or reject hotel listings on the marketplace</p>
            </div>
            
            <div className="flex gap-2 mb-6">
              {['', 'pending', 'verified', 'rejected'].map(f => (
                <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm"
                        onClick={() => setFilter(f)}>
                  {f || 'All'}
                </Button>
              ))}
            </div>

            <GlassCard data-testid="admin-hotel-verification-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hotel</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Wallet</TableHead>
                    <TableHead>Trust Score</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredHotels.map(h => (
                    <TableRow key={h.id}>
                      <TableCell className="font-medium">{h.name}</TableCell>
                      <TableCell className="text-sm">{h.city}, {h.country}</TableCell>
                      <TableCell className="mono text-xs">{h.wallet_address ? `${h.wallet_address.slice(0, 8)}...` : 'None'}</TableCell>
                      <TableCell>
                        <span className={`font-medium mono ${h.trust_score >= 80 ? 'text-emerald-400' : h.trust_score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                          {h.trust_score}/100
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={h.verification_status === 'verified' ? 'default' : h.verification_status === 'rejected' ? 'destructive' : 'secondary'}>
                          {h.verification_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {h.verification_status !== 'verified' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-400 border-emerald-400/30"
                                    onClick={() => verifyHotel(h.id, 'verified')}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Verify
                            </Button>
                          )}
                          {h.verification_status !== 'rejected' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs text-red-400 border-red-400/30"
                                    onClick={() => verifyHotel(h.id, 'rejected')}>
                              <XCircle className="w-3 h-3 mr-1" /> Reject
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GlassCard>
          </TabsContent>

          <TabsContent value="billing" className="mt-6">
            <AdminBillingTab />
          </TabsContent>

          <TabsContent value="availability" className="mt-6">
            <AdminAvailabilityTab />
          </TabsContent>

        </Tabs>
      </main>
    </div>
  );
}
